import { ulid } from 'ulid';

import type {
	CreateCustomerInput,
	UpdateCustomerInput
} from '@/graphql/generated';
import { shouldIncludeItem, toOptionalString } from '@/lib/formHelpers';

import type {
	CustomerAddressFormValue,
	CustomerBankDetailFormValue,
	CustomerBeneficiaryFormValue,
	CustomerContactFormValue,
	CustomerFormValues,
	CustomerVehicleFormValue
} from './customer-form';

const normaliseContactTypeId = (value?: string) => {
	if (value == null || value === '') {
		return null;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const normaliseAddressTypeId = normaliseContactTypeId;

const shouldIncludeBankDetail = (detail: CustomerBankDetailFormValue) => {
	return shouldIncludeItem(
		detail,
		['bankName', 'accountHolderName', 'accountNumber', 'iban', 'bankCode'],
		'bankDetailsId'
	);
};

const shouldIncludeContact = (contact: CustomerContactFormValue) => {
	return shouldIncludeItem(contact, 'contactValue', 'customerContactId');
};

const buildCreateContacts = (contacts: CustomerContactFormValue[]) => {
	return contacts
		.filter(contact => shouldIncludeContact(contact))
		.map(contact => {
			const contactTypeId = normaliseContactTypeId(contact.contactTypeId);
			if (contactTypeId == null) {
				return null;
			}

			return {
				customerContactId: ulid(),
				contactTypeId,
				contactValue: toOptionalString(contact.contactValue),
				isPrimary: Boolean(contact.isPrimary),
				notes: toOptionalString(contact.notes)
			};
		})
		.filter((value): value is NonNullable<typeof value> => Boolean(value));
};

const shouldIncludeAddress = (address: CustomerAddressFormValue) => {
	return shouldIncludeItem(
		address,
		['streetAddress', 'city', 'state', 'postalCode', 'country'],
		'customerAddressId'
	);
};

const buildCreateAddresses = (addresses: CustomerAddressFormValue[]) => {
	return addresses
		.filter(address => shouldIncludeAddress(address))
		.map(address => {
			const addressTypeId = normaliseAddressTypeId(address.addressTypeId);

			if (addressTypeId == null) {
				return null;
			}

			return {
				customerAddressId: ulid(),
				addressId: ulid(), // Generate new addressId for create
				addressTypeId,
				streetAddress: toOptionalString(address.streetAddress),
				city: toOptionalString(address.city),
				state: toOptionalString(address.state),
				postalCode: toOptionalString(address.postalCode),
				country: toOptionalString(address.country)
			};
		})
		.filter((value): value is NonNullable<typeof value> => Boolean(value));
};

const buildCreateBankDetails = (bankDetails: CustomerBankDetailFormValue[]) => {
	return bankDetails
		.filter(detail => shouldIncludeBankDetail(detail))
		.map(detail => ({
			bankDetailsId: ulid(),
			bankName: toOptionalString(detail.bankName),
			accountNumber: toOptionalString(detail.accountNumber),
			iban: toOptionalString(detail.iban),
			bankCode: toOptionalString(detail.bankCode),
			accountHolderName: toOptionalString(detail.accountHolderName),
			isPrimary: Boolean(detail.isPrimary)
		}));
};

const buildUpdateContacts = (contacts: CustomerContactFormValue[]) => {
	return contacts
		.filter(contact => shouldIncludeContact(contact))
		.filter(contact => !contact.isDeleted) // Filter out deleted contacts instead of marking them
		.map(contact => {
			const contactTypeId = normaliseContactTypeId(contact.contactTypeId);
			if (contactTypeId == null) {
				return null;
			}

			return {
				customerContactId: contact.customerContactId || ulid(),
				contactTypeId,
				contactValue: toOptionalString(contact.contactValue),
				isPrimary: Boolean(contact.isPrimary),
				notes: toOptionalString(contact.notes)
			};
		})
		.filter(Boolean) as Array<{
		customerContactId: string;
		contactTypeId: number;
		contactValue: string | null;
		isPrimary: boolean;
		notes: string | null;
	}>;
};

const buildUpdateAddresses = (addresses: CustomerAddressFormValue[]) => {
	return addresses
		.filter(address => shouldIncludeAddress(address))
		.filter(address => !address.isDeleted) // Filter out deleted addresses instead of marking them
		.map(address => {
			const addressTypeId = normaliseAddressTypeId(address.addressTypeId);
			const addressId = address.addressId?.trim() || ulid();
			const customerAddressId = address.customerAddressId?.trim() || ulid();

			if (addressTypeId == null) {
				return null;
			}

			return {
				addressId,
				customerAddressId,
				addressTypeId,
				streetAddress: toOptionalString(address.streetAddress),
				city: toOptionalString(address.city),
				state: toOptionalString(address.state),
				postalCode: toOptionalString(address.postalCode),
				country: toOptionalString(address.country)
			};
		})
		.filter((value): value is NonNullable<typeof value> => value !== null);
};

const buildUpdateBankDetails = (bankDetails: CustomerBankDetailFormValue[]) => {
	return bankDetails
		.filter(detail => shouldIncludeBankDetail(detail))
		.filter(detail => !detail.isDeleted) // Filter out deleted bank details instead of marking them
		.map(detail => ({
			bankDetailsId: detail.bankDetailsId || ulid(),
			bankName: toOptionalString(detail.bankName),
			accountNumber: toOptionalString(detail.accountNumber),
			iban: toOptionalString(detail.iban),
			bankCode: toOptionalString(detail.bankCode),
			accountHolderName: toOptionalString(detail.accountHolderName),
			isPrimary: Boolean(detail.isPrimary)
		}));
};

const buildCreateBeneficiaries = (
	beneficiaries: CustomerBeneficiaryFormValue[]
) => {
	return beneficiaries
		.map(beneficiary => {
			const beneficiaryCustomerId =
				beneficiary.beneficiaryCustomerId?.trim();

			if (!beneficiaryCustomerId) {
				return null;
			}

			return {
				beneficiaryCustomerId,
				customerBeneficiaryId: ulid(),
				isActive: Boolean(beneficiary.isActive),
				notes: toOptionalString(beneficiary.notes),
				relationshipType: toOptionalString(beneficiary.relationshipType)
			};
		})
		.filter(Boolean) as Array<{
		customerBeneficiaryId: string;
		beneficiaryCustomerId: string;
		isActive: boolean;
		notes: string | null;
		relationshipType: string | null;
	}>;
};

const buildCreateVehicles = (vehicles: CustomerVehicleFormValue[]) => {
	return vehicles
		.map(vehicle => {
			const plateNumber = vehicle.plateNumber?.trim();
			const vehicleTypeId = vehicle.vehicleTypeId
				? Number(vehicle.vehicleTypeId)
				: null;

			// Require either plateNumber or make/model
			if (!plateNumber && !vehicle.make && !vehicle.model) {
				return null;
			}

			return {
				vehicleId: ulid(),
				plateNumber: toOptionalString(plateNumber),
				vehicleTypeId,
				make: toOptionalString(vehicle.make),
				model: toOptionalString(vehicle.model),
				color: toOptionalString(vehicle.color),
				isActive: Boolean(vehicle.isActive)
			};
		})
		.filter(Boolean) as Array<{
		vehicleId: string;
		plateNumber: string | null;
		vehicleTypeId: number | null;
		make: string | null;
		model: string | null;
		color: string | null;
		isActive: boolean;
	}>;
};

const buildUpdateVehicles = (vehicles: CustomerVehicleFormValue[]) => {
	return vehicles
		.filter(vehicle => !vehicle.isDeleted)
		.map(vehicle => {
			const plateNumber = vehicle.plateNumber?.trim();
			const vehicleTypeId = vehicle.vehicleTypeId
				? Number(vehicle.vehicleTypeId)
				: null;

			// Require either plateNumber or make/model
			if (!plateNumber && !vehicle.make && !vehicle.model) {
				return null;
			}

			return {
				vehicleId: vehicle.vehicleId?.trim() || ulid(),
				plateNumber: toOptionalString(plateNumber),
				vehicleTypeId,
				make: toOptionalString(vehicle.make),
				model: toOptionalString(vehicle.model),
				color: toOptionalString(vehicle.color),
				isActive: Boolean(vehicle.isActive)
			};
		})
		.filter(Boolean) as Array<{
		vehicleId: string;
		plateNumber: string | null;
		vehicleTypeId: number | null;
		make: string | null;
		model: string | null;
		color: string | null;
		isActive: boolean;
	}>;
};

const buildUpdateBeneficiaries = (
	beneficiaries: CustomerBeneficiaryFormValue[]
) => {
	return beneficiaries
		.filter(beneficiary => !beneficiary.isDeleted) // Filter out deleted beneficiaries instead of marking them
		.map(beneficiary => {
			const beneficiaryCustomerId =
				beneficiary.beneficiaryCustomerId?.trim();

			if (!beneficiaryCustomerId) {
				return null;
			}

			return {
				customerBeneficiaryId:
					beneficiary.customerBeneficiaryId?.trim() || ulid(),
				beneficiaryCustomerId,
				isActive: Boolean(beneficiary.isActive),
				notes: toOptionalString(beneficiary.notes),
				relationshipType: toOptionalString(
					beneficiary.relationshipType
				)
			};
		})
		.filter(Boolean) as Array<{
		customerBeneficiaryId: string;
		beneficiaryCustomerId: string;
		isActive: boolean;
		notes: string | null;
		relationshipType: string | null;
	}>;
};

export const buildCreateCustomerInput = (
	formValues: CustomerFormValues,
	customerId: string
): CreateCustomerInput => {
	return {
		customerId,
		customerCategoryId: formValues.customerCategoryId,
		isCompany: formValues.isCompany,
		isActive: formValues.isActive,
		marketingOptIn: formValues.marketingOptIn,
		firmName: toOptionalString(formValues.firmName),
		firstName: toOptionalString(formValues.firstName),
		lastName: toOptionalString(formValues.lastName),
		email: toOptionalString(formValues.email),
		fiscalNumber: toOptionalString(formValues.fiscalNumber),
		preferredLanguage: toOptionalString(formValues.preferredLanguage),
		companyRegistrationNumber: toOptionalString(
			formValues.companyRegistrationNumber
		),
		industry: toOptionalString(formValues.industry),
		companySize: toOptionalString(formValues.companySize),
		notes: toOptionalString(formValues.notes),
		contacts: buildCreateContacts(formValues.contacts ?? []),
		addresses: buildCreateAddresses(formValues.addresses ?? []),
		bankDetails: buildCreateBankDetails(formValues.bankDetails ?? []),
		beneficiaries: buildCreateBeneficiaries(formValues.beneficiaries ?? []),
		vehicles: buildCreateVehicles(formValues.vehicles ?? [])
	};
};

export const buildUpdateCustomerInput = (
	formValues: CustomerFormValues,
	customerId: string
): UpdateCustomerInput => {
	const contacts = buildUpdateContacts(formValues.contacts ?? []);
	const addresses = buildUpdateAddresses(formValues.addresses ?? []);
	const bankDetails = buildUpdateBankDetails(formValues.bankDetails ?? []);
	const beneficiaries = buildUpdateBeneficiaries(formValues.beneficiaries ?? []);
	const vehicles = buildUpdateVehicles(formValues.vehicles ?? []);

	return {
		customerId,
		customerCategoryId: formValues.customerCategoryId,
		isActive: formValues.isActive,
		marketingOptIn: formValues.marketingOptIn,
		firmName: toOptionalString(formValues.firmName),
		firstName: toOptionalString(formValues.firstName),
		lastName: toOptionalString(formValues.lastName),
		email: toOptionalString(formValues.email),
		fiscalNumber: toOptionalString(formValues.fiscalNumber),
		preferredLanguage: toOptionalString(formValues.preferredLanguage),
		companyRegistrationNumber: toOptionalString(
			formValues.companyRegistrationNumber
		),
		industry: toOptionalString(formValues.industry),
		companySize: toOptionalString(formValues.companySize),
		notes: toOptionalString(formValues.notes),
		contacts,
		addresses,
		bankDetails,
		beneficiaries,
		vehicles
	};
};

export const generateCustomerId = () => ulid();
